import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import OutsideClickHandler from 'react-outside-click-handler';
import config from '@/config';
import api from '@/shared/api';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import intlShape from '@/shared/utils/intlShape';
import EditableToggle from './EditableToggle';
import MultiSelect from '../input/multiselect/MultiSelect';

const messages = defineMessages({
  AddTeam: { id: 'Add a team', defaultMessage: 'Add a team' },
  TeamsSetByCompany: { id: 'TeamsSetByCompany', defaultMessage: 'Teams set by company' },
});

const isEditableTeam = team => !team.isCompanyLocked || team.keepAfterCompanyChange;

const EditableEmployeeTeamSelector = ({ value, onChange, readOnly, intl }) => {
  const [allTeams, setAllTeams] = useState([]);

  useEffect(() => {
    api.base.get(`${config.appUrls.api}/api/team/flatTeams`).then(({ data }) => {
      setAllTeams((data || []).sort(dynamicSort('name')));
    });
  }, []);

  const teams = value || [];
  const editableTeams = teams.filter(isEditableTeam);
  const lockedTeams = teams.filter(t => t.isCompanyLocked);

  const displayValue = teams.length ? (
    <span className="bold">{teams.map(t => t.name).join(', ')}</span>
  ) : null;

  const save = selected => onChange(selected.map(t => ({ id: t.id, name: t.name, code: t.code })));

  return (
    <EditableToggle
      onSubmit={save}
      value={editableTeams}
      readOnly={readOnly}
      displayValue={displayValue}
    >
      {({ inputValue, setInputValue, submit }) => {
        const selected = inputValue || [];
        const addTeam = team => {
          if (!team || selected.some(t => t.id === team.id)) return;
          setInputValue([...selected, team]);
        };
        return (
          <OutsideClickHandler onOutsideClick={submit}>
            <MultiSelect
              items={allTeams
                .filter(t => !selected.some(s => s.id === t.id))
                .map(t => ({ ...t, label: t.name }))}
              selectedItem={selected.map(t => ({ ...t, label: t.name }))}
              onItemSelected={item => addTeam(item)}
              onItemRemoved={item => setInputValue(selected.filter(t => t.id !== item.value.id))}
              placeholder={intl.formatMessage(messages.AddTeam)}
            />
            {lockedTeams.length > 0 && (
              <div className="margin5T">
                <small className="text-muted">
                  {intl.formatMessage(messages.TeamsSetByCompany)}
                </small>
                <ul className="list-unstyled margin0">
                  {lockedTeams.map(t => (
                    <li key={t.id}>{t.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </OutsideClickHandler>
        );
      }}
    </EditableToggle>
  );
};

EditableEmployeeTeamSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  intl: intlShape.isRequired,
};

EditableEmployeeTeamSelector.defaultProps = {
  value: [],
  readOnly: false,
};

export default injectIntl(EditableEmployeeTeamSelector);
